import React, { useMemo, useEffect, useState } from 'react';
import { Box, TextField, LoadingSpinner } from 'edenred-ui';
import { useFormContext, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { getFrequencyLabel } from '@epi-pages/Beneficiaries/helpers/getFrequencyLabel';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import { getBenefitIcon } from '@epi-constants/benefitTypes';
import { createRules, FormFields } from '@epi-forms/helpers';
import { required } from '@epi-helpers/formHelpers/validators';
import { WarningProcessingOfEmployees } from '@epi-shared/components';

import {
  BenefitListContainer,
  BenefitListHeader,
  LimitColumn,
  BenefitRow,
  BenefitInfo,
  BenefitLimit,
  FrequencyLabel,
  BenefitGroupListItem
} from './Benefits.styles';

export const BenefitGroupSelector = ({
  isGroupManagementAllowed
}: {
  isGroupManagementAllowed: boolean;
}) => {
  const { t } = useTranslation();
  const { primaryBackColorLight, sizeMedium } = useTheme();
  const { control, trigger } = useFormContext();
  const { data: benefitGroups, isLoading } = useGetBenefitGroupsQuery();
  const [isFocused, setIsFocused] = useState(false);
  const {
    field: { value: selectedGroupId, onChange },
    fieldState: { error }
  } = useController({
    name: FormFields.GroupId,
    control,
    rules: createRules([required])
  });

  const groupOptions = useMemo(() => {
    return benefitGroups?.map(group => ({
      id: group.groupId,
      label: group.groupName
    }));
  }, [benefitGroups]);

  const selectedGroup = useMemo(() => {
    trigger(FormFields.GroupId);
    if (!selectedGroupId) return null;
    return benefitGroups?.find(group => group.groupId === selectedGroupId);
  }, [benefitGroups, selectedGroupId]);

  useEffect(() => {
    if (benefitGroups?.length === 1 && !selectedGroupId) {
      onChange(benefitGroups[0].groupId);
    }
  }, [benefitGroups, selectedGroupId, onChange]);

  const formatAmount = (amount: number) => {
    return amount.toFixed(2).replace('.', ',');
  };

  const getLabelText = () => {
    if (isFocused || selectedGroupId) {
      return t('containers.beneficiaries.detail.benefit_section.benefit_group');
    }
    return t(
      'containers.beneficiaries.detail.benefit_section.select_benefit_group'
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <Box>
      <Box pt={2.5}>
        <WarningProcessingOfEmployees isHidden={isGroupManagementAllowed} />
      </Box>
      <Box maxWidth={400} py={2.5}>
        <TextField
          id="benefit-group-select"
          label={getLabelText()}
          select
          value={selectedGroupId || ''}
          onChange={e => onChange(Number(e.target.value))}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          error={!!error}
          helperText={error?.message}
          fullWidth
        >
          {groupOptions?.map(option => (
            <BenefitGroupListItem key={option.id} value={option.id}>
              {option.label}
            </BenefitGroupListItem>
          ))}
        </TextField>
      </Box>

      {selectedGroup && (
        <BenefitListContainer>
          <BenefitListHeader>
            <Box flex={1}>
              {t(
                'containers.beneficiaries.detail.benefit_section.benefits_in_group'
              )}
            </Box>
            <LimitColumn>
              {t('containers.beneficiaries.detail.benefit_section.limit')}
            </LimitColumn>
          </BenefitListHeader>

          {selectedGroup.benefitSettings.map(benefit => (
            <BenefitRow key={benefit.benefitType}>
              <BenefitInfo>
                {getBenefitIcon(t, benefit.benefitType, primaryBackColorLight)}
                <Box fontSize={sizeMedium}>
                  {t(
                    `components.manage_benefits.new.benefits.${benefit.benefitType.toLowerCase()}`
                  )}
                </Box>
              </BenefitInfo>
              <LimitColumn>
                <BenefitLimit>
                  {formatAmount(benefit.maxAmountPerPeriod)} €
                  <FrequencyLabel>
                    ({getFrequencyLabel(benefit.benefitType)})
                  </FrequencyLabel>
                </BenefitLimit>
              </LimitColumn>
            </BenefitRow>
          ))}
        </BenefitListContainer>
      )}
    </Box>
  );
};
